import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { Card, Table, Button, Tag, Space, Tooltip, Select, Input, DatePicker, Typography, Tabs, Modal } from "antd";
import { EyeOutlined, CheckOutlined, CloseOutlined, FilterFilled } from "@ant-design/icons";
import moment from "moment";
import useRequest from "../hooks/useRequest";
import useDebounce from "../hooks/useDebounce";
import { ShowToast, Severty } from "../helper/toast";
import ViewWithdrawalModal from "../components/ViewWithdrawalModal";

const { Search } = Input;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;
const { TabPane } = Tabs;

function WithdrawalRequestManager() {
  const { request } = useRequest();
  const requestRef = useRef(request);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [ownerType, setOwnerType] = useState("user"); // 'user' | 'merchant'
  const [rejectReasonModal, setRejectReasonModal] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const { current, pageSize } = pagination;

  const [filters, setFilters] = useState({
    status: null,
    kyc_status: null,
    min_amount: null,
    max_amount: null,
    dateRange: null,
  });
  const [sortConfig, setSortConfig] = useState({
    sort_by: null,
    sort_order: null,
  });

  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText, 400);
  const [filtersVisible, setFiltersVisible] = useState(true);
  const [rejectReason, setRejectReason] = useState("");
  const [selectedRejectId, setSelectedRejectId] = useState(null);
  useEffect(() => {
    requestRef.current = request;
  }, [request]);

  // ---------------- FETCH DATA ----------------
  const fetchWithdrawals = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.append("owner_type", ownerType);
    params.append("page", current);
    params.append("limit", pageSize);
    if (filters.status) params.append("status", filters.status);
    if (filters.kyc_status) params.append("kyc_status", filters.kyc_status);
    if (filters.min_amount) params.append("min_amount", filters.min_amount);
    if (filters.max_amount) params.append("max_amount", filters.max_amount);
    if (debouncedSearch) params.append("search", debouncedSearch.trim());
    if (filters.dateRange?.length === 2) {
      params.append("from_date", filters.dateRange[0].format("YYYY-MM-DD"));
      params.append("to_date", filters.dateRange[1].format("YYYY-MM-DD"));
    }
    if (sortConfig.sort_by && sortConfig.sort_order) {
      params.append("sort_by", sortConfig.sort_by);
      params.append("sort_order", sortConfig.sort_order);
    }

    requestRef.current({
      url: `/api/admin/withdrawals?${params.toString()}`,
      method: "GET",
      onSuccess: (res) => {
        setData(Array.isArray(res?.data) ? res.data : []);
        setPagination((prev) => ({
          ...prev,
          current: Number(res?.pagination?.page) || prev.current,
          pageSize: Number(res?.pagination?.limit) || prev.pageSize,
          total: Number(res?.pagination?.total) || 0,
        }));
        setLoading(false);
      },
      onError: () => {
        setLoading(false);
        ShowToast("Failed to load withdrawals", Severty.ERROR);
      },
    });
  }, [
    ownerType,
    current,
    pageSize,
    filters,
    debouncedSearch,
    sortConfig.sort_by,
    sortConfig.sort_order,
  ]);

  const getWithdrawalDetails = useCallback((id) => {
    requestRef.current({
      url: `/api/admin/withdrawal/${id}`,
      method: "GET",
      onSuccess: (res) => {
        setSelectedWithdrawal(res.data);
        setViewModalOpen(true);
      },
      onError: () => {
        ShowToast("Failed to load withdrawal details", Severty.ERROR);
      },
    });
  }, []);

  const handleRejectApproveWithdrawal = useCallback((id, action, reason = null) => {
    let data = {
      action: action,
    }
    if (action === "REJECT" && reason && reason.trim()) {
      data.reason = reason;
    }
    requestRef.current({
      url: `/api/admin/withdrawal/${id}`,
      method: "PUT",
      data: data,
      onSuccess: () => {
        fetchWithdrawals();
        const msg = action === "REJECT" ? "Withdrawal rejected successfully" : "Withdrawal approved successfully";
        ShowToast(msg, Severty.SUCCESS);
      },
      onError: (err) => {
        const errorMsg = err?.response?.data?.message || err?.message || `Failed to ${action.toLowerCase()} withdrawal`;
        ShowToast(errorMsg, Severty.ERROR);
      },
    });
  }, [fetchWithdrawals]);

  const handleClearFilters = useCallback(() => {
    setSearchText("");
    setFilters({
      status: null,
      kyc_status: null,
      min_amount: null,
      max_amount: null,
      dateRange: null,
    });
    setSortConfig({ sort_by: null, sort_order: null });
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, []);

  useEffect(() => {
    fetchWithdrawals();
  }, [fetchWithdrawals]);

  const statusColor = {
    PENDING: "orange",
    "UNDER REVIEW": "blue",
    COMPLETED: "green",
    REJECTED: "red",
    CANCELLED: "volcano",
  };
  // ---------------- TABLE COLUMNS ----------------
  const columns = [
    {
      title: "S. No.",
      render: (_, __, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
      width: 80,
    },
    {
      title: "Request ID",
      dataIndex: "request_id",
      render: (id) => <Text code>{id}</Text>
    },
    {
      title: ownerType === "user" ? "User" : "Merchant",
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{r?.name || r?.owner?.name || "N/A"}</Text>
          <Text type="secondary">{r?.email || r?.owner?.email || "N/A"}</Text>
          <Text type="secondary">{r?.mobile || r?.owner?.mobile || "N/A"}</Text>
        </Space>
      ),
    },
    {
      title: "Wallet Balance",
      dataIndex: "wallet_balance_at_request",
      render: (b) => `NZ$ ${b}`,
    },
    {
      title: "Withdrawal Amount",
      dataIndex: "amount_requested",
      render: (a) => `NZ$ ${a}`
    },
    {
      title: ownerType === "user" ? "KYC" : "KYB",
      render: (_, r) => (
        <Tag color={statusColor[r.kyc_status] || "default"}>
          {r.kyc_status}
        </Tag>
      ),
    },
    {
      title: "Bank",
      dataIndex: "bank_name",
      // render: (s) => <Tag color={s === "VERIFIED" ? "green" : "red"}>{s}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (s) => {
        const color =
          s === "PROCESSING"
            ? "gold"
            : s === "COMPLETED"
              ? "green"
              : s === "REJECTED"
                ? "red"
                : s === "CANCELLED"
                  ? "volcano"
                  : "blue";
        return <Tag color={color}>{s}</Tag>;
      },
    },
    {
      title: "Requested At",
      dataIndex: "created_at",
      render: (d) => moment(d).format("DD MMM YYYY, hh:mm A")
    },
    {
      title: "Action",
      render: (_, r) => (
        <Space>
          <Tooltip title="View">
            <Button
              className="button-action-small"
              icon={<EyeOutlined />}
              onClick={() => {
                getWithdrawalDetails(r.id);
              }}
            />
          </Tooltip>
          {r.status === "PROCESSING" && (
            <>
              <Tooltip title="Approve">
                <Button
                  className="button-action-small"
                  type="primary"
                  icon={<CheckOutlined />}
                  onClick={() => {
                    handleRejectApproveWithdrawal(r.id, "APPROVE");
                  }}
                />
              </Tooltip>
              <Tooltip title="Reject">
                <Button
                  className="button-action-small"
                  danger
                  icon={<CloseOutlined />}
                  onClick={() => {
                    setRejectReasonModal(true);
                    setSelectedRejectId(r.id);
                    // handleRejectApproveWithdrawal(r.id, "REJECT");
                  }}
                />
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  const isValidReason = useMemo(() => rejectReason.trim().length > 0, [rejectReason]);

  return (
    <Card
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <span>Withdrawal Request Manager</span>
          <Button
            icon={<FilterFilled />}
            onClick={() => setFiltersVisible((prev) => !prev)}
          >
            {filtersVisible ? "Hide Filters" : "Show Filters"}
          </Button>
        </div>
      }
    >
      <Tabs
        activeKey={ownerType === "user" ? "user" : "merchant"}
        onChange={(key) => {
          const nextOwnerType = key === "user" ? "user" : "merchant";
          setOwnerType(nextOwnerType);
          setFilters({
            status: null,
            kyc_status: null,
            min_amount: null,
            max_amount: null,
            dateRange: null,
          });
          setPagination((prev) => ({ ...prev, current: 1 }));
        }}
        items={[
          { key: "user", label: "User Withdrawals" },
          { key: "merchant", label: "Merchant Withdrawals" },
        ]}
      />
      {filtersVisible && (
        <div style={{ marginBottom: 16 }}>
          <Space
            direction="horizontal"
            style={{ width: "100%", flexWrap: "wrap" }}
            size="middle"
          >
            <Search
              placeholder="Search by ID / Email / Mobile"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setPagination((p) => ({ ...p, current: 1 }));
              }}
            />
            <RangePicker
              style={{ minWidth: 220 }}
              value={filters.dateRange}
              onChange={(d) => {
                setFilters((f) => ({ ...f, dateRange: d }));
                setPagination((p) => ({ ...p, current: 1 }));
              }}
            />

            <Select
              placeholder={ownerType === "user" ? "KYC Status" : "KYB Status"}
              allowClear
              style={{ minWidth: 160 }}
              value={filters.kyc_status}
              onChange={(v) => {
                setFilters((f) => ({ ...f, kyc_status: v }));
                setPagination((p) => ({ ...p, current: 1 }));
              }}
            >
              <Option value="VERIFIED">Verified</Option>
              <Option value="COMPLETED">Completed</Option>
              <Option value="PENDING">Pending</Option>
              <Option value="REJECTED">Rejected</Option>
            </Select>

            <Select
              placeholder="Status"
              allowClear
              style={{ minWidth: 160 }}
              value={filters.status}
              onChange={(v) => {
                setFilters((f) => ({ ...f, status: v }));
                setPagination((p) => ({ ...p, current: 1 }));
              }}
            >
              <Option value="PENDING">Pending</Option>
              <Option value="PROCESSING">Processing</Option>
              <Option value="COMPLETED">Completed</Option>
              <Option value="CANCELLED">Cancelled</Option>
              <Option value="REJECTED">Rejected</Option>
            </Select>

            <Input
              type="number"
              min={0}
              placeholder="Min Amount"
              style={{ minWidth: 140 }}
              value={filters.min_amount ?? ""}
              onChange={(e) => {
                const value = e.target.value;
                setFilters((f) => ({ ...f, min_amount: value || null }));
                setPagination((p) => ({ ...p, current: 1 }));
              }}
            />
            <Input
              type="number"
              min={0}
              placeholder="Max Amount"
              style={{ minWidth: 140 }}
              value={filters.max_amount ?? ""}
              onChange={(e) => {
                const value = e.target.value;
                setFilters((f) => ({ ...f, max_amount: value || null }));
                setPagination((p) => ({ ...p, current: 1 }));
              }}
            />
            <Button onClick={handleClearFilters} disabled={!filters.status && !filters.kyc_status && !filters.min_amount && !filters.max_amount && !filters.dateRange && !searchText}>Clear Filters</Button>
          </Space>
        </div>
      )}
      <div className="table-responsive">
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
          }}
          onChange={(p, _tableFilters, sorter) => {
            const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
            const orderMap = {
              ascend: "ASC",
              descend: "DESC",
            };
            const sortOrder = orderMap[activeSorter?.order] || null;
            const sortBy = sortOrder ? activeSorter?.field : null;

            setSortConfig({ sort_by: sortBy, sort_order: sortOrder });
            setPagination((prev) => ({
              ...prev,
              current: p.current,
              pageSize: p.pageSize,
            }));
          }}
        />
      </div>
      <ViewWithdrawalModal
        open={viewModalOpen}
        data={selectedWithdrawal}
        onClose={() => {
          setViewModalOpen(false);
          setSelectedWithdrawal(null);
        }}
      />
      <Modal
        title="Reject Withdrawal"
        open={rejectReasonModal}
        onCancel={() => setRejectReasonModal(false)}
        onOk={() => {
          if (!rejectReason.trim()) {
            ShowToast("Please enter reject reason", Severty.WARNING);
            return;
          }
          handleRejectApproveWithdrawal(selectedRejectId, "REJECT", rejectReason);
          setRejectReasonModal(false);
          setRejectReason("");
          setSelectedRejectId(null);
        }}
        okText="Submit"
        cancelText="Cancel"
        okButtonProps={{ danger: true, disabled: !isValidReason || !rejectReason.trim() }}
      >
        <Input.TextArea
          rows={4}
          placeholder="Enter rejection reason..."
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
        />
      </Modal>
    </Card>
  );
}

export default WithdrawalRequestManager;
